<template>
  <header class="tw-mb-11">
    <h2 class="tw-text-[1.7rem] tw-font-bold">
      {{ t('auth.titles.reset_password').toUpperCase() }}
    </h2>
    <p class="tw-text-base tw-font-light">{{ t('auth.titles.enter_new_password') }}</p>
  </header>

  <div
    v-if="errorMessage"
    class="tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded tw-mb-4"
    role="alert"
  >
    {{ errorMessage }}
  </div>

  <Form
    class="tw-flex tw-flex-col"
    :validation-schema="newPasswordSchema"
    v-slot="{ errors, isSubmitting }"
    @submit="onNewPasswordCreated"
  >
    <div class="tw-mb-6">
      <label for="password" class="tw-text-[0.9rem] tw-mb-1 tw-block">
        {{ t('auth.titles.password') }}
      </label>

      <div class="tw-relative">
        <Field
          id="password"
          name="password"
          :type="isPasswordShown ? 'text' : 'password'"
          class="tw-w-full tw-border tw-border-[#d8d8d8] tw-rounded-lg tw-text-[0.9rem] tw-px-3 tw-py-1.5 placeholder:tw-text-[#dcdcdc] placeholder:tw-text-[0.9rem] focus:tw-outline-none"
          :class="{ 'tw-border-danger': errors.password }"
          :placeholder="t('auth.titles.enter_password')"
        />

        <div v-if="errors.password" class="tw-text-danger tw-text-xs tw-mt-1">
          {{ errors.password }}
        </div>

        <i
          v-if="!isPasswordShown"
          class="icon-eye tw-absolute tw-top-2 tw-right-2.5 tw-cursor-pointer before:tw-text-[#38678c]"
          @click="togglePasswordVisibility"
        ></i>
        <i
          v-else
          class="icon-eye-slash tw-absolute tw-top-2 tw-right-2.5 tw-cursor-pointer before:tw-text-[#38678c]"
          @click="togglePasswordVisibility"
        ></i>
      </div>
    </div>

    <div class="tw-mb-6">
      <label for="confirmPassword" class="tw-text-[0.9rem] tw-mb-1 tw-block">
        {{ t('auth.titles.confirm_password') }}
      </label>

      <div class="tw-relative">
        <Field
          id="confirmPassword"
          name="confirmPassword"
          :type="isConfirmPasswordShown ? 'text' : 'password'"
          class="tw-w-full tw-border tw-border-[#d8d8d8] tw-rounded-lg tw-text-[0.9rem] tw-px-3 tw-py-1.5 placeholder:tw-text-[#dcdcdc] placeholder:tw-text-[0.9rem] focus:tw-outline-none"
          :class="{ 'tw-border-danger': errors.confirmPassword }"
          :placeholder="t('auth.titles.enter_password')"
        />

        <div v-if="errors.confirmPassword" class="tw-text-danger tw-text-xs tw-mt-1">
          {{ errors.confirmPassword }}
        </div>

        <i
          v-if="!isConfirmPasswordShown"
          class="icon-eye tw-absolute tw-top-2 tw-right-2.5 tw-cursor-pointer before:tw-text-[#38678c]"
          @click="toggleConfirmPasswordVisibility"
        ></i>
        <i
          v-else
          class="icon-eye-slash tw-absolute tw-top-2 tw-right-2.5 tw-cursor-pointer before:tw-text-[#38678c]"
          @click="toggleConfirmPasswordVisibility"
        ></i>
      </div>
    </div>

    <button
      class="tw-bg-cta-primary tw-text-white tw-rounded-lg tw-min-h-[2.4rem] disabled:tw-opacity-70"
      :disabled="isSubmitting"
    >
      {{ t('auth.titles.reset_password') }}
    </button>
  </Form>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useAuthStore } from '@/stores/auth.store';
import { storeToRefs } from 'pinia';

import { Form, Field } from 'vee-validate';
import { object, string, ref as YupRef } from 'yup';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const newPasswordSchema = object().shape({
  password: string()
    .required(t('auth.titles.enter_password_msg'))
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,}$/,
      t('auth.forms.password_invalid')
    ),
  confirmPassword: string()
    .required(t('auth.titles.confirm_password_msg'))
    .oneOf([YupRef('password'), null, ''], t('auth.forms.passwords_not_match')),
});

const isPasswordShown = ref(false);
const isConfirmPasswordShown = ref(false);

const emit = defineEmits<{
  (e: 'nextChallenge', nextChallengeType: string): void;
}>();

const { nextChallenge, errorMessage } = storeToRefs(useAuthStore());
const { createNewPassword } = useAuthStore();

const onNewPasswordCreated = async (values: any) => {
  await createNewPassword(values.password);

  emit('nextChallenge', nextChallenge.value);
};

const togglePasswordVisibility = () => {
  isPasswordShown.value = !isPasswordShown.value;
};
const toggleConfirmPasswordVisibility = () => {
  isConfirmPasswordShown.value = !isConfirmPasswordShown.value;
};
</script>

<style scoped>
/* Using Tailwind classes - no additional styles needed */
</style>
